import {createSelector} from 'reselect'
import get from 'lodash/get.js'
import isEqual from 'lodash/isEqual.js'
import format from 'date-fns/format'
import endOfDay from 'date-fns/endOfDay'
import isDateEqual from 'date-fns/isEqual'
import subMonths from 'date-fns/subMonths'
import startOfDay from 'date-fns/startOfDay'

import {getTimeZone} from '../../common/date.js'
import {ensureArray, ensureArrayOfNumbers} from '../../common/ensure.js'
import {stringifyURL} from '../../common/querystring.js'
import {formsSelector} from '../../store.js'
import {
  FINANCIAL_PANEL,
  SHIPPING_PANEL,
  CUSTOMER_PANEL,
  PRODUCT_PANEL,
  ANALYTICS_URI_COMPONENT,
  ANALYTICS_PANELS,
  DEFAULT_PANEL,
  DEFAULT_DATE_TYPE,
  INVENTORY_PANEL,
} from '../../common/constants/Analytics.js'
import {
  hasAnalyticsCustomerPermissionSelector,
  hasAnalyticsFinancialPermissionSelector,
  hasAnalyticsInventoryPermissionSelector,
  hasAnalyticsPanelPermissionSelector,
  hasAnalyticsPermissionSelector,
  hasAnalyticsProductPermissionSelector,
  hasAnalyticsShippingPermissionSelector,
} from '../../data/me.js'
import {cartOptionsSelector} from '../../data/carts.js'
import {supplierOptionsSelector} from '../../data/suppliers.js'
import {warehouseOptionsSelector} from '../../data/warehouses.js'
import {locationSelector} from '../../redux/selectors/ui/location.js'
import {
  showFeatureLocksSelector,
  useAnalyticsSelector,
} from '../../data/company.js'
import {financialExportToCSVParamsSelector} from './financialSelectors.js'
import {productExportToCSVParamsSelector} from './productSelectors.js'
import {shippingExportToCSVParamsSelector} from './shippingSelectors.js'

export const ANALYTICS = 'ANALYTICS'

export const canUseAnalyticsSelector = createSelector(
  hasAnalyticsPermissionSelector,
  useAnalyticsSelector,
  (hasPermission, hasAnalyticsFeature) => hasPermission && hasAnalyticsFeature,
)

export function showAnalyticsNavSelector(state) {
  const showFeatureLocks = showFeatureLocksSelector(state)
  const hasAnalyticsFeature = useAnalyticsSelector(state)
  const hasAnalyticsPermission = hasAnalyticsPermissionSelector(state)

  return hasAnalyticsPermission && (hasAnalyticsFeature || showFeatureLocks)
}

export const analyticsFormSelector = createSelector(
  formsSelector,
  (forms) => forms[ANALYTICS],
)

export function analyticsPanelSelector(state) {
  const {pathComponents} = locationSelector(state)
  const panel = pathComponents[1] || DEFAULT_PANEL
  const hasPermission = hasAnalyticsPanelPermissionSelector(state, {panel})

  if (
    hasPermission &&
    pathComponents[0] === ANALYTICS_URI_COMPONENT &&
    ANALYTICS_PANELS.includes(panel)
  ) {
    return panel
  }

  return null
}

export function analyticsQuerySelector(state) {
  analyticsQuerySelector.cache = analyticsQuerySelector.cache || {}
  const cache = analyticsQuerySelector.cache

  const {query} = locationSelector(state)

  if (query === cache.query) {
    return cache.result
  }

  cache.query = query

  const result = {
    cart_id: ensureArrayOfNumbers(query.cart_id || query.cartFilters),
    supplier_id: ensureArrayOfNumbers(
      query.supplier_id || query.supplierFilters,
    ),
    sku: ensureArray(query.sku || query.productFilters),
    warehouse_id: ensureArrayOfNumbers(query.warehouse_id),
    start_date:
      query.start_date || query.startDate
        ? new Date(query.start_date || query.startDate)
        : null,
    end_date:
      query.end_date || query.endDate
        ? new Date(query.end_date || query.endDate)
        : null,
    date_type: query.date_type || query.dateType || DEFAULT_DATE_TYPE,
  }

  if (isEqual(cache.result, result)) {
    return cache.result
  }

  cache.result = result

  return result
}

export function cartFiltersSelector(state) {
  cartFiltersSelector.cache = cartFiltersSelector.cache || {}
  const cache = cartFiltersSelector.cache

  const {cart_id} = analyticsQuerySelector(state)

  if (isEqual(cache.result, cart_id)) {
    return cache.result
  }

  cache.result = cart_id

  return cart_id
}

export function supplierFiltersSelector(state) {
  supplierFiltersSelector.cache = supplierFiltersSelector.cache || {}
  const cache = supplierFiltersSelector.cache

  const {supplier_id} = analyticsQuerySelector(state)

  if (isEqual(cache.result, supplier_id)) {
    return cache.result
  }

  cache.result = supplier_id

  return supplier_id
}

export function productFiltersSelector(state) {
  productFiltersSelector.cache = productFiltersSelector.cache || {}
  const cache = productFiltersSelector.cache

  const {sku} = analyticsQuerySelector(state)

  if (isEqual(cache.result, sku)) {
    return cache.result
  }

  cache.result = sku

  return sku
}

export function warehouseFiltersSelector(state) {
  warehouseFiltersSelector.cache = warehouseFiltersSelector.cache || {}
  const cache = warehouseFiltersSelector.cache

  const {warehouse_id} = analyticsQuerySelector(state)

  if (isEqual(cache.result, warehouse_id)) {
    return cache.result
  }

  cache.result = warehouse_id

  return warehouse_id
}

export function startDateSelector(state) {
  startDateSelector.cache = startDateSelector.cache || {}
  const cache = startDateSelector.cache

  const {start_date} = analyticsQuerySelector(state)

  if (isDateEqual(cache.result, start_date)) {
    return cache.result
  }

  cache.result = start_date

  return start_date
}

export function endDateSelector(state) {
  endDateSelector.cache = endDateSelector.cache || {}
  const cache = endDateSelector.cache

  const {end_date} = analyticsQuerySelector(state)

  if (isDateEqual(cache.result, end_date)) {
    return cache.result
  }

  cache.result = end_date

  return end_date
}

export function dateTypeSelector(state) {
  dateTypeSelector.cache = dateTypeSelector.cache || {}
  const cache = dateTypeSelector.cache

  const {date_type} = analyticsQuerySelector(state)

  if (isEqual(cache.result, date_type)) {
    return cache.result
  }

  cache.result = date_type

  return date_type
}

export function isLoadingAnalyticsSelector(state) {
  return !!get(analyticsFormSelector(state), 'isLoading')
}

export function isProductSearchLoadingSelector(state) {
  return !!get(analyticsFormSelector(state), 'isProductSearchLoading')
}

function formatDate(filter) {
  return `${format(new Date(filter.start_date), 'yyyy-MM-dd')}_${format(
    new Date(filter.end_date),
    'yyyy-MM-dd',
  )}`
}

export function exportToCSVParamsSelector(state, {type}) {
  const params = queryParamsSelector(state)
  const date = formatDate(params)

  return {
    ...financialExportToCSVParamsSelector(state, {type, date}),
    ...shippingExportToCSVParamsSelector(state, {type, date}),
    ...productExportToCSVParamsSelector(state, {type, date}),
  }
}

export function missingSKUsSelector(state) {
  const {sku} = analyticsQuerySelector(state)
  const topSellersData = get(
    analyticsFormSelector(state),
    'order_item_financials__product_search',
  )

  return sku.reduce((prev, sku) => {
    if (!topSellersData.find((bucket) => bucket.sku === sku)) {
      prev.push(sku)
    }

    return prev
  }, [])
}

export const activeCartFiltersSelector = createSelector(
  cartFiltersSelector,
  cartOptionsSelector,
  (cartIDs, cartOptions) =>
    cartIDs.map((id) => cartOptions.find((option) => option.entity.id === id)),
)

export const activeSupplierFiltersSelector = createSelector(
  supplierFiltersSelector,
  supplierOptionsSelector,
  (supplierIDs, supplierOptions) =>
    supplierIDs.map((id) =>
      supplierOptions.find((option) => option.entity.id === id),
    ),
)

export const activeWarehouseFiltersSelector = createSelector(
  warehouseFiltersSelector,
  warehouseOptionsSelector,
  (warehouseIDs, warehouseOptions) =>
    warehouseIDs.map((id) =>
      warehouseOptions.find((option) => option.entity.id === id),
    ),
)

export const analyticsHashParamsSelector = createSelector(
  analyticsPanelSelector,
  analyticsQuerySelector,
  (state, props) => props && props.panel,
  (
    currentPanel,
    {cart_id, supplier_id, warehouse_id, sku, start_date, end_date, date_type},
    panel,
  ) => {
    const params = {
      cart_id,
      supplier_id,
      warehouse_id,
      sku,
      start_date: start_date ? start_date.toISOString() : undefined,
      end_date: end_date ? end_date.toISOString() : undefined,
    }

    // panel is only set if building from panelLinkSelector
    panel = panel || currentPanel

    if (panel === SHIPPING_PANEL && DEFAULT_DATE_TYPE !== date_type) {
      params.date_type = date_type
    }

    return params
  },
)

export function analyticsHashBuilder(panel, params) {
  const hash =
    panel === DEFAULT_PANEL
      ? `#/${ANALYTICS_URI_COMPONENT}`
      : `#/${ANALYTICS_URI_COMPONENT}/${panel}`

  return stringifyURL(hash, params)
}

export const panelLinkSelector = createSelector(
  (state, {panel}) => panel,
  analyticsHashParamsSelector,
  analyticsHashBuilder,
)

export const defaultDateRangeSelector = createSelector(
  () => format(new Date(), 'yyyy-MM-dd'), // update selector every day
  () => ({
    start_date: startOfDay(subMonths(new Date(), 1)).toISOString(),
    end_date: endOfDay(new Date()).toISOString(),
  }),
)

export const defaultPanelSelector = createSelector(
  hasAnalyticsFinancialPermissionSelector,
  hasAnalyticsShippingPermissionSelector,
  hasAnalyticsInventoryPermissionSelector,
  hasAnalyticsProductPermissionSelector,
  hasAnalyticsCustomerPermissionSelector,
  (
    hasAnalyticsFinancialPermission,
    hasAnalyticsShippingPermission,
    hasAnalyticsInventoryPermission,
    hasAnalyticsProductPermission,
    hasAnalyticsCustomerPermission,
  ) =>
    hasAnalyticsFinancialPermission
      ? FINANCIAL_PANEL
      : hasAnalyticsShippingPermission
        ? SHIPPING_PANEL
        : hasAnalyticsInventoryPermission
          ? INVENTORY_PANEL
          : hasAnalyticsProductPermission
            ? PRODUCT_PANEL
            : hasAnalyticsCustomerPermission
              ? CUSTOMER_PANEL
              : null,
)

export const defaultAnalyticsHashSelector = createSelector(
  defaultPanelSelector,
  defaultDateRangeSelector,
  analyticsHashBuilder,
)

export function queryParamsSelector(state) {
  const panel = analyticsPanelSelector(state)
  const {
    cart_id,
    supplier_id,
    sku,
    warehouse_id,
    start_date,
    end_date,
    date_type,
  } = analyticsQuerySelector(state)

  const filters = {}

  if (
    [FINANCIAL_PANEL, SHIPPING_PANEL, CUSTOMER_PANEL, PRODUCT_PANEL].includes(
      panel,
    )
  ) {
    filters.cart_id = cart_id
  }

  if ([PRODUCT_PANEL].includes(panel)) {
    filters.supplier_id = supplier_id
    filters.sku = sku
  }

  if (
    [FINANCIAL_PANEL, SHIPPING_PANEL, CUSTOMER_PANEL, PRODUCT_PANEL].includes(
      panel,
    )
  ) {
    if (start_date) {
      filters.start_date = start_date.toISOString()
    }
    if (end_date) {
      filters.end_date = endOfDay(end_date).toISOString()
    }
    filters.timezone = getTimeZone()
  }

  if ([SHIPPING_PANEL].includes(panel)) {
    filters.date_type = date_type
  }

  if ([INVENTORY_PANEL].includes(panel)) {
    filters.warehouse_id = warehouse_id
  }

  return filters
}
